import { useState, useEffect, useRef } from "react";
import "./BrandServiceCenter.css";
import {
  Hexagon,
  ArrowLeft,
  Package,
  MapPin,
  Phone,
  CalendarX,
  Search,
} from "lucide-react";
import Breadcrums from "../../components/breadcrums_v2/Breadcrums";
import ApiService from "../../components/Api_services"; // 假设你有这个
import FallbackImg from "../../components/FallbackImg";
import CustomDropdown from "../../components/customdropdown_v2/CustomDropdown";

const BrandServiceCenterv2 = () => {
  const [brands, setBrands] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [groupOptions, setGroupOptions] = useState([]);
  const [searchProductGrp, setSearchProductGrp] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [statesOptions, setStatesOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const lastStatesFetchKey = useRef(null);

  useEffect(() => {
    const fetchBrands = async (product_group_code, state, city) => {
      setLoading(true);
      try {
        const res = await ApiService.getBrandsServiceCentre(
          product_group_code,
          state,
          city,
        );
        if (res && res.status === 200) {
          setBrands(res.data);
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };

    const productGroupCode =
      selectedGroup && selectedGroup.value !== "all"
        ? selectedGroup.value
        : null;

    fetchBrands(productGroupCode, selectedState, selectedCity);
  }, [selectedGroup, selectedState, selectedCity]);

  const fetchServiceCentreStates = async (brand_id, product_group_code) => {
    try {
      const res = await ApiService.getServiceCentreStates(
        brand_id,
        product_group_code,
      );
      if (res && res.status === 200) {
        const options = (res.data || []).map((s) => ({
          value: s,
          label: s,
        }));
        setStatesOptions([{ value: "", label: "All States" }, ...options]);
      }
    } catch (error) {
      console.error("Error fetching service centre states:", error);
      setStatesOptions([]);
    }
  };

  const fetchServiceCentreCities = async (
    brand_id,
    state,
    product_group_code,
  ) => {
    try {
      const res = await ApiService.getServiceCentreCities(
        brand_id,
        state,
        product_group_code,
      );
      if (res && res.status === 200) {
        const options = (res.data || []).map((c) => ({
          value: c,
          label: c,
        }));
        setCitiesOptions([{ value: "", label: "All Cities" }, ...options]);
      }
    } catch (error) {
      console.error("Error fetching service centre cities:", error);
      setCitiesOptions([]);
    }
  };

  const filteredBrands = brands.filter((b) =>
    b.brand_name.toLowerCase().includes(search.toLowerCase()),
  );

  const formatAddress = (sc) => {
    const parts = [sc?.street, sc?.city, sc?.state, sc?.postcode].filter(
      Boolean,
    );
    return parts.join(", ");
  };

  const formatOffday = (offday) => {
    if (!offday) return "";
    const day = offday.split(/[\s:-]/)[0];
    return `  ${day}`;
  };

  const getPhoneNumber = (sc) => {
    return sc?.phone_no || sc?.fixed_line || sc?.mobile || "";
  };

  const hasAllRequiredData = (sc) => {
    const address = formatAddress(sc);
    const phone = getPhoneNumber(sc);
    const offday = formatOffday(sc.offday);

    return !!(sc?.pic && address && phone && offday);
  };

  // Step 3: fetch states only when brand+group combo actually changes (not on data refresh)
  useEffect(() => {
    if (selectedService && selectedGroup) {
      const key = `${selectedService.brand_id}_${selectedGroup.value}`;
      if (lastStatesFetchKey.current !== key) {
        lastStatesFetchKey.current = key;
        setSelectedState(null);
        setSelectedCity(null);
        setCitiesOptions([]);

        const productGroupCode =
          selectedGroup.value !== "all" ? selectedGroup.value : null;

        fetchServiceCentreStates(selectedService.brand_id, productGroupCode);
      }
    } else {
      lastStatesFetchKey.current = null;
    }
  }, [selectedService, selectedGroup]);

  // Only fetch cities once a state has been picked
  useEffect(() => {
    if (selectedService && selectedState) {
      setSelectedCity(null);

      const productGroupCode =
        selectedGroup && selectedGroup.value !== "all"
          ? selectedGroup.value
          : null;

      fetchServiceCentreCities(
        selectedService.brand_id,
        selectedState,
        productGroupCode,
      );
    } else {
      setCitiesOptions([]);
    }
  }, [selectedState]);

  // Keep selectedService in sync with the latest fetched brands data
  useEffect(() => {
    if (!selectedService) return;
    const updated = brands.find((b) => b.brand_id === selectedService.brand_id);
    if (updated) {
      setSelectedService(updated);
    }
  }, [brands]);

  const ServiceCenterList = ({ sc, index }) => {
    const address = formatAddress(sc);
    const phone = getPhoneNumber(sc);
    const offday = formatOffday(sc.offday);

    return (
      <a
        className="sc-card"
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in Google Maps"
      >
        <div className="sc-header">
          <div className="sc-badge">{index + 1}</div>
          <strong className="sc-name">{sc.pic}</strong>
        </div>

        <div className="sc-address-box">
          <MapPin size={20} />
          <div>
            <div className="sc-label">ADDRESS</div>
            <div className="sc-text">{address}</div>
          </div>
        </div>

        <div className="sc-footer">
          <div className="sc-info-col">
            <Phone size={20} />
            <div>
              <div className="sc-label">PHONE</div>
              <div className="sc-text">{phone}</div>
            </div>
          </div>
          <div className="sc-info-col">
            <CalendarX size={20} />
            <div>
              <div className="sc-label">REST DAY</div>
              <div className="sc-text">{offday}</div>
            </div>
          </div>
        </div>
      </a>
    );
  };

  const hasValidCentre =
    selectedService?.service_centres?.some(hasAllRequiredData);

  useEffect(() => {
    if (!selectedService) return;
    const options = selectedService.service_centres
      .flatMap((sc) => sc.product_groups ?? [])
      .map((pg) => ({
        value: pg.code,
        label: pg.name,
        photo_url: pg.photo_url,
      }));

    const unique = [...new Map(options.map((o) => [o.value, o])).values()];

    setGroupOptions([{ value: "all", label: "All", photo_url: "" }, ...unique]);
  }, [selectedService]);

  const handleGroupClick = (option) => {
    setSelectedGroup(option);
  };

  return (
    <div className="brandservicecenter_v2">
      <Breadcrums />
      <div className="brandservicecenter-content_v2">
        <div className="brandservicecenter-title-container_v2">
          <div className="brandservicecenter-title_v2">
            SERVICE CENTER LOCATIONS
          </div>
        </div>

        {/* Step 1 */}
        {!selectedService && (
          <div className="brandservicecenter-main_v2">
            <div className="searchbar-brandserv-container_v2">
              <div style={{ fontSize: 20, fontWeight: 600 }}>
                Select by Brand
              </div>
              <div className="searchbar-brandserv_v2">
                <Search color={"#798777"} size={16} style={{ width: "40px" }} />
                <input
                  type="text"
                  placeholder="Search brands..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div
                  className="searchbar-brandserv-remove_v2"
                  style={{
                    visibility: search ? "visible" : "hidden",
                    pointerEvents: search ? "auto" : "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSearch("");
                  }}
                >
                  &#x2715;
                </div>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", margin: "40px 0" }}>
                Loading...
              </div>
            ) : filteredBrands.length > 0 ? (
              <div className="brandserv-item-container_v2">
                {filteredBrands.map((brand, idx) => (
                  <div className="brandserv-item-inner_v2">
                    <div
                      key={brand.brand_id}
                      className="brandserv-item_v2"
                      onClick={() => {
                        setSelectedService(brand);
                      }}
                    >
                      <FallbackImg
                        src={brand.service_centres_photo}
                        alt={brand.brand_name}
                        style={{
                          width: "150px",
                          height: "80px",
                          objectFit: "contain",
                          borderRadius: "10px",
                          padding: "20px",
                        }}
                      />
                    </div>
                    <span>{brand.brand_name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data-message">
                <Hexagon size={64} color={"#d4d4d4"} />
                <span className="no-data-message-title">No brand found</span>
                <span>Please contact customer support for assistance</span>
              </div>
            )}
          </div>
        )}

        {/* Step 2 */}
        {selectedService && !selectedGroup && (
          <div className="brandservicecenter-main_v2">
            <div className="searchbar-brandserv-container_v2">
              <button
                className="brandservicecenter-back_v2"
                onClick={() => setSelectedService(null)}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              {groupOptions && (
                <div className="searchbar-brandserv_v2">
                  <Search
                    color={"#798777"}
                    size={16}
                    style={{ width: "40px" }}
                  />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchProductGrp}
                    onChange={(e) => setSearchProductGrp(e.target.value)}
                  />
                  <div
                    className="searchbar-brandserv-remove_v2"
                    style={{
                      visibility: searchProductGrp ? "visible" : "hidden",
                      pointerEvents: searchProductGrp ? "auto" : "none",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSearchProductGrp("");
                    }}
                  >
                    &#x2715;
                  </div>
                </div>
              )}
            </div>

            {groupOptions.length === 0 ? (
              <div className="no-data-message">
                <Package size={64} color={"#d4d4d4"} />
                <span className="no-data-message-title">No product found</span>
                <span>Please contact customer support for assistance</span>
              </div>
            ) : (
              <div className="brandserv-item-container_v2">
                {groupOptions
                  .filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchProductGrp.toLowerCase()),
                  )
                  .map((option) => (
                    <div className="brandserv-item-inner_v2">
                      <div
                        className="brandserv-item_v2"
                        key={option.value}
                        onClick={() => handleGroupClick(option)}
                      >
                        {option.value !== "all" ? (
                          <FallbackImg
                            src={option.photo_url}
                            alt={option.label}
                            width="80"
                            height="80"
                          />
                        ) : (
                          <Package size={80} />
                        )}
                      </div>
                      <span>{option.label}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3 */}
        {selectedGroup && (
          <div className="brandservicecenter-main_v2">
            <div className="searchbar-brandserv-container_v2">
              <button
                className="brandservicecenter-back_v2"
                onClick={() => setSelectedGroup(null)}
              >
                <ArrowLeft size={16} />
                Back
              </button>

              <div className="brandserv-dropdown-container_v2">
                <CustomDropdown
                  options={statesOptions}
                  value={selectedState}
                  onChange={(val) => setSelectedState(val || null)}
                  placeholder="All States"
                  className="brandserv-dropdown_v2"
                  hideSearchbar={true}
                />

                <CustomDropdown
                  options={citiesOptions}
                  value={selectedCity}
                  onChange={(val) => setSelectedCity(val || null)}
                  placeholder="All Cities"
                  className="brandserv-dropdown_v2"
                  hideSearchbar={true}
                  disabled={!selectedState}
                />
              </div>
            </div>

            {!hasValidCentre ? (
              <div className="no-data-message">
                <MapPin size={64} color={"#d4d4d4"} />
                <span className="no-data-message-title">
                  No service center found
                </span>
                <span>Please contact customer support for assistance</span>
              </div>
            ) : (
              selectedService.service_centres
                .filter((sc) =>
                  selectedGroup.value === "all"
                    ? true
                    : sc.product_groups?.some(
                        (pg) => pg.code === selectedGroup.value,
                      ),
                )
                .map((sc, idx) => (
                  <ServiceCenterList key={idx} sc={sc} index={idx} />
                ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandServiceCenterv2;
